import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Sparkles, PlayCircle, BookOpen, X, Loader2, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';
import AssistantChat from './AssistantChat';

const DIFF_COLORS = { facile: '#00FF88', moyen: '#FFB800', 'avancé': '#FF6B35', avance: '#FF6B35' };

const TutorialModal = ({ id, onClose }) => {
  const [tuto, setTuto] = useState(null);
  useEffect(() => {
    api.get(`/assistant/tutorials/${id}`).then((r) => setTuto(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tutorial-modal">
      <div className="bg-[#121214] border border-[#3A3A3D] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#3A3A3D] sticky top-0 bg-[#121214]">
          <h3 className="text-white font-bold">{tuto?.title || 'Chargement…'}</h3>
          <button onClick={onClose} className="text-[#B8B8BA] hover:text-white" aria-label="Fermer" data-testid="tutorial-modal-close"><X className="h-5 w-5" /></button>
        </div>
        {!tuto ? <div className="p-8 text-center"><Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin mx-auto" /></div> : (
          <div className="p-4 space-y-4 text-sm">
            {tuto.short_answer && <p className="text-[#E8E8E8]">{tuto.short_answer}</p>}
            {tuto.prerequisites?.length > 0 && (
              <div>
                <p className="text-[#6B6B6E] text-xs mb-1">Prérequis</p>
                <ul className="list-disc list-inside text-[#B8B8BA] space-y-0.5">{tuto.prerequisites.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
            )}
            {tuto.steps?.length > 0 && (
              <div>
                <p className="text-[#6B6B6E] text-xs mb-1">Étapes</p>
                <ol className="list-decimal list-inside text-[#E8E8E8] space-y-1">{tuto.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              </div>
            )}
            {tuto.alternative_solutions?.length > 0 && (
              <div>
                <p className="text-[#6B6B6E] text-xs mb-1">Solutions alternatives</p>
                <ul className="list-disc list-inside text-[#B8B8BA] space-y-0.5">{tuto.alternative_solutions.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const TutorialsAssistant = () => {
  const [catalog, setCatalog] = useState({ devices: [], apps: [], categories: [] });
  const [filters, setFilters] = useState({ device: '', app: '', category: '', difficulty: '', content: '', sort: 'popular' });
  const [tutorials, setTutorials] = useState([]);
  const [loadingTutos, setLoadingTutos] = useState(false);
  const [novaQuestion, setNovaQuestion] = useState('');
  const [novaKey, setNovaKey] = useState(0);
  const [modalId, setModalId] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const novaRef = useRef(null);

  useEffect(() => {
    api.get('/assistant/catalog').then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const loadTutorials = useCallback(() => {
    setLoadingTutos(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    api.get(`/assistant/tutorials?${params.toString()}`)
      .then((r) => setTutorials(r.data.items || []))
      .catch(() => setTutorials([]))
      .finally(() => setLoadingTutos(false));
  }, [filters]);

  useEffect(() => { loadTutorials(); }, [loadTutorials]);

  const askNova = (question) => {
    setNovaQuestion(question);
    setNovaKey((k) => k + 1);
    setTimeout(() => novaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) askNova(search.trim());
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: f[k] === v ? '' : v }));

  return (
    <div className="mb-16" data-testid="tutorials-assistant">
      {/* Barre de recherche en langage naturel */}
      <form onSubmit={submitSearch} className="max-w-3xl mx-auto mb-6" data-testid="nl-search-form">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B6B6E]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Posez votre question : « Où entrer mes identifiants dans TiviMate ? »"
            className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-2xl pl-12 pr-28 py-4 text-white placeholder-[#6B6B6E] focus:border-[#FF6B35] focus:outline-none"
            data-testid="nl-search-input"
          />
          <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white text-sm font-medium" data-testid="nl-search-submit">
            Demander
          </button>
        </div>
      </form>

      {/* Zone Nova */}
      <motion.div
        ref={novaRef}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-12"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[#FF6B35]" />
          <h2 className="text-lg font-bold text-white">Besoin d'aide ? Demandez à Nova</h2>
        </div>
        <div className="relative bg-[#121214] border border-[#3A3A3D] rounded-2xl overflow-hidden" style={{ height: '560px' }} data-testid="tutorials-nova">
          <AssistantChat key={novaKey} initialQuestion={novaQuestion} />
        </div>
      </motion.div>

      {/* Filtres dynamiques */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">Parcourir les tutoriels</h2>
        <button onClick={() => setShowFilters((s) => !s)} className="md:hidden flex items-center gap-2 text-sm text-[#B8B8BA]" data-testid="toggle-filters">
          <Filter className="h-4 w-4" /> Filtres
        </button>
      </div>

      <div className={`${showFilters ? 'block' : 'hidden'} md:block mb-6 space-y-3`} data-testid="tutorials-filters">
        <div className="flex flex-wrap gap-2">
          {['popular', 'recent', 'useful'].map((s) => (
            <button key={s} onClick={() => setFilters((f) => ({ ...f, sort: s }))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filters.sort === s ? 'border-[#FF6B35] text-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] text-[#B8B8BA]'}`}
              data-testid={`sort-${s}`}>
              {s === 'popular' ? 'Populaires' : s === 'recent' ? 'Récents' : 'Les plus utiles'}
            </button>
          ))}
          {['video', 'text'].map((c) => (
            <button key={c} onClick={() => setFilter('content', c)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filters.content === c ? 'border-[#00FFFF] text-[#00FFFF] bg-[#00FFFF]/10' : 'border-[#3A3A3D] text-[#B8B8BA]'}`}>
              {c === 'video' ? 'Avec vidéo' : 'Texte'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={filters.device} onChange={(e) => setFilters((f) => ({ ...f, device: e.target.value }))} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white" data-testid="filter-device">
            <option value="">Tous les appareils</option>
            {catalog.devices.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.app} onChange={(e) => setFilters((f) => ({ ...f, app: e.target.value }))} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white" data-testid="filter-app">
            <option value="">Toutes les applications</option>
            {catalog.apps.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white" data-testid="filter-category">
            <option value="">Toutes les catégories</option>
            {catalog.categories.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
          <select value={filters.difficulty} onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white" data-testid="filter-difficulty">
            <option value="">Toutes difficultés</option>
            {['facile', 'moyen', 'avancé'].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* Cartes tutoriels */}
      {loadingTutos ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin mx-auto" /></div>
      ) : tutorials.length === 0 ? (
        <p className="text-[#6B6B6E] text-sm py-8 text-center">Aucun tutoriel ne correspond. Posez plutôt votre question à Nova ci-dessus.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tutorials.map((t) => (
            <div key={t.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4 flex flex-col hover:border-[#FF6B35]/40 transition-colors" data-testid="tutorial-card">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-white font-semibold text-sm leading-snug">{t.title}</h3>
                {t.has_video && <PlayCircle className="h-4 w-4 text-[#FF3B5C] flex-shrink-0" title="Vidéo disponible" />}
              </div>
              <p className="text-[#B8B8BA] text-xs mb-3 line-clamp-2 flex-1">{t.description}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {t.devices?.slice(0, 1).map((d) => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-[#00FFFF]/10 text-[#00FFFF]">{d}</span>)}
                {t.apps?.slice(0, 1).map((a) => <span key={a} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35]">{a}</span>)}
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${DIFF_COLORS[t.difficulty] || '#6B6B6E'}20`, color: DIFF_COLORS[t.difficulty] || '#6B6B6E' }}>{t.difficulty}</span>
                <span className="text-[10px] text-[#6B6B6E]">~{t.estimated_minutes} min · 👁 {t.views}</span>
              </div>
              <div className="flex gap-2 mt-auto">
                <button onClick={() => setModalId(t.id)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#222226] text-white hover:bg-[#2A2A2D]" data-testid="read-tutorial-btn">
                  <BookOpen className="h-3.5 w-3.5" /> Lire
                </button>
                <button onClick={() => askNova(`J'ai besoin d'aide : ${t.title}`)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35]/15 text-[#FF6B35] hover:bg-[#FF6B35]/25" data-testid="ask-nova-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Aide de Nova
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalId && <TutorialModal id={modalId} onClose={() => setModalId(null)} />}
    </div>
  );
};

export default TutorialsAssistant;
